import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {yupResolver} from '@hookform/resolvers/yup';
import {useStore} from 'effector-react';
import {Controller, FieldError, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';
import {toast} from 'react-toastify';
import * as yup from 'yup';

import {createNkEmployee, fetchNkEmployees, updateNkEmployee} from 'api';
import {BackTo} from 'components/common/BackTo';
import {Page} from 'components/common/Page';
import {ButtonVariant} from 'components/ui-kit/Button';
import {Input} from 'components/ui-kit/Input';
import {ContentContainer} from 'components/ui-kit/PageComponents';
import {Select, SelectOption} from 'components/ui-kit/Select';
import {RestError, showRestError} from 'core/errors';
import {history} from 'core/history';
import {ROUTE} from 'core/routes';
import {profile$} from 'models/auth';
import {
    NkEmployee,
    NkEmployeeCreateRequest,
    NkEmployeeRole,
    NkEmployeeUpdateRequest,
    UserRoles,
} from 'types';
import {ProfileType} from 'types/profile';

import {Actions, Field, Form, FormFields, RequiredMark, SubmitButton, CancelButton} from './styled';

interface Props {
    mode: 'create' | 'edit';
}

interface FormValues {
    firstName: string;
    middleName: string;
    lastName: string;
    email: string;
    phone: string;
    role: NkEmployeeRole;
}

const getDefaultValues = (): FormValues => ({
    firstName: '',
    middleName: '',
    lastName: '',
    email: '',
    phone: '',
    role: 'EMPLOYEE',
});

export const PersonnelNkEmployeeForm = ({mode}: Props) => {
    const {t, i18n} = useTranslation();
    const {id} = useParams<{id?: string}>();
    const profile = useStore(profile$);
    const [employee, setEmployee] = useState<NkEmployee | null>(null);
    const [isLoading, setIsLoading] = useState(mode === 'edit');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const canAssignAdministrator = Boolean(profile?.roles.includes(UserRoles.ROLE_MPL_NK_DIRECTOR));

    const requiredMessage = useCallback(() => t('pages.register.validations.required'), [
        i18n.language,
    ]);
    const schema = useMemo(
        () =>
            yup.object().shape({
                firstName: yup
                    .string()
                    .trim()
                    .required(requiredMessage()),
                middleName: yup.string(),
                lastName: yup
                    .string()
                    .trim()
                    .required(requiredMessage()),
                email: yup
                    .string()
                    .trim()
                    .email()
                    .required(requiredMessage()),
                phone: yup
                    .string()
                    .trim()
                    .required(requiredMessage())
                    .matches(/^\+?[1-9]\d{1,14}$/, t('pages.register.validations.phone')),
                role: yup
                    .mixed<NkEmployeeRole>()
                    .oneOf(['EMPLOYEE', 'ADMINISTRATOR'])
                    .required(),
            }),
        [requiredMessage, t],
    );
    const {
        control,
        formState: {errors},
        handleSubmit,
        reset,
    } = useForm<FormValues>({
        resolver: yupResolver(schema),
        mode: 'all',
        defaultValues: getDefaultValues(),
    });

    useEffect(() => {
        let isMounted = true;

        const loadData = async () => {
            setIsLoading(true);
            try {
                const employees = mode === 'edit' ? await fetchNkEmployees() : [];
                if (!isMounted) return;

                if (mode === 'edit') {
                    const currentEmployee = employees.find((item) => item.userId === Number(id));
                    if (!currentEmployee) {
                        history.replace(ROUTE.PERSONNEL_NK);
                        return;
                    }
                    setEmployee(currentEmployee);
                    reset({
                        firstName: currentEmployee.firstName || '',
                        middleName: currentEmployee.middleName || '',
                        lastName: currentEmployee.lastName || '',
                        email: currentEmployee.email || '',
                        phone: currentEmployee.phone || '',
                        role: currentEmployee.role || 'EMPLOYEE',
                    });
                }
            } catch (error) {
                if (isMounted) showRestError(error as RestError);
            } finally {
                if (isMounted) setIsLoading(false);
            }
        };

        loadData().catch(() => undefined);
        return () => {
            isMounted = false;
        };
    }, [id, mode, reset]);

    const roleOptions = useMemo<SelectOption<NkEmployeeRole>[]>(() => {
        const options: SelectOption<NkEmployeeRole>[] = [
            {value: 'EMPLOYEE', label: t('pages.profile.personnelNk.roles.EMPLOYEE')},
        ];
        if (canAssignAdministrator || employee?.role === 'ADMINISTRATOR') {
            options.push({
                value: 'ADMINISTRATOR',
                label: t('pages.profile.personnelNk.roles.ADMINISTRATOR'),
            });
        }
        return options;
    }, [canAssignAdministrator, employee, t]);
    const isAdministratorRoleLocked = !canAssignAdministrator && employee?.role === 'ADMINISTRATOR';

    const onSubmit = async (values: FormValues) => {
        if (mode === 'edit' && !employee) return;

        setIsSubmitting(true);
        try {
            if (mode === 'create') {
                const request: NkEmployeeCreateRequest = {
                    registrationRequest: {
                        email: values.email,
                        phone: values.phone,
                        type: ProfileType.EMPLOYEE,
                        first_name: values.firstName,
                        ...(values.middleName && {middle_name: values.middleName}),
                        last_name: values.lastName,
                    },
                    role: values.role,
                };
                await createNkEmployee(request);
            } else if (employee) {
                const request: NkEmployeeUpdateRequest = {
                    user: {
                        phone: {value: values.phone, reset: false},
                        firstName: {value: values.firstName, reset: false},
                        middleName: {value: values.middleName, reset: !values.middleName},
                        lastName: {value: values.lastName, reset: false},
                    },
                    role: values.role,
                };
                await updateNkEmployee({id: employee.userId, request});
            }
            toast.success(
                t(
                    `pages.profile.personnelNk.form.${
                        mode === 'create' ? 'createSuccess' : 'updateSuccess'
                    }`,
                ),
            );
            history.push(ROUTE.PERSONNEL_NK);
        } catch (error) {
            showRestError(error as RestError);
        } finally {
            setIsSubmitting(false);
        }
    };

    const getError = (name: keyof FormValues) => (errors[name] as FieldError | undefined)?.message;
    const fieldTranslation = (name: string) => t(`pages.profile.personnelNk.form.fields.${name}`);

    return (
        <Page title={t('pages.profile.title')}>
            <BackTo to={ROUTE.PERSONNEL_NK}>{t('pages.profile.personnelNk.form.back')}</BackTo>
            <ContentContainer>
                <Form onSubmit={handleSubmit(onSubmit)}>
                    <FormFields>
                        {(['firstName', 'middleName', 'lastName', 'email', 'phone'] as const).map(
                            (name) => (
                                <Field key={name} text={getError(name)} isError={!!errors[name]}>
                                    <span>
                                        {fieldTranslation(name)}
                                        {name !== 'middleName' && <RequiredMark>*</RequiredMark>}
                                    </span>
                                    <Controller
                                        name={name}
                                        control={control}
                                        render={(props) => (
                                            <Input
                                                {...props}
                                                disabled={
                                                    isLoading ||
                                                    (mode === 'edit' && name === 'email')
                                                }
                                                hasError={!!errors[name]}
                                            />
                                        )}
                                    />
                                </Field>
                            ),
                        )}
                        <Field text={getError('role')} isError={!!errors.role}>
                            <span>
                                {fieldTranslation('role')}
                                <RequiredMark>*</RequiredMark>
                            </span>
                            <Controller
                                name="role"
                                control={control}
                                render={({value, onChange, onBlur}) => (
                                    <Select
                                        value={
                                            roleOptions.find((option) => option.value === value) ||
                                            null
                                        }
                                        options={roleOptions}
                                        onBlur={onBlur}
                                        onChange={(option: SelectOption<NkEmployeeRole> | null) =>
                                            onChange(option?.value || 'EMPLOYEE')
                                        }
                                        disabled={isLoading || isAdministratorRoleLocked}
                                        hasError={!!errors.role}
                                    />
                                )}
                            />
                        </Field>
                    </FormFields>
                    <Actions>
                        <CancelButton
                            type="button"
                            variant={ButtonVariant.Outline}
                            onClick={() => history.push(ROUTE.PERSONNEL_NK)}
                            disabled={isSubmitting}
                        >
                            {t('pages.profile.personnelNk.form.cancel')}
                        </CancelButton>
                        <SubmitButton
                            type="submit"
                            isLoading={isSubmitting}
                            disabled={isLoading || (mode === 'edit' && !employee)}
                        >
                            {t(
                                `pages.profile.personnelNk.form.${
                                    mode === 'create' ? 'create' : 'save'
                                }`,
                            )}
                        </SubmitButton>
                    </Actions>
                </Form>
            </ContentContainer>
        </Page>
    );
};
